import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Recycle, Download, Mail, Search, Filter, Calendar, TrendingDown, Eye, ArrowLeft, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { calculationsAPI } from '../api/client';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import { format, parseISO } from 'date-fns';
import { lt } from 'date-fns/locale';

const CalculationHistory = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [calculations, setCalculations] = useState([]);
  const [filteredCalculations, setFilteredCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterByAmount, setFilterByAmount] = useState('all');

  useEffect(() => {
    loadCalculations();
  }, []);

  useEffect(() => {
    filterAndSortCalculations();
  }, [calculations, searchQuery, sortBy, filterByAmount]);

  const loadCalculations = async () => {
    setLoading(true);
    try {
      const response = await calculationsAPI.getHistory();
      setCalculations(response.data);
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko užkrauti istorijos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortCalculations = () => {
    let filtered = [...calculations];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(calc => {
        const searchLower = searchQuery.toLowerCase();
        const dateStr = format(parseISO(calc.created_at), 'yyyy-MM-dd HH:mm', { locale: lt });
        const amountStr = calc.total_final_tax.toFixed(2);
        return dateStr.includes(searchLower) || amountStr.includes(searchQuery);
      });
    }

    // Amount filter
    if (filterByAmount !== 'all') {
      filtered = filtered.filter(calc => {
        const amount = calc.total_final_tax;
        switch(filterByAmount) {
          case 'low': return amount < 100;
          case 'medium': return amount >= 100 && amount < 500;
          case 'high': return amount >= 500;
          default: return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_desc':
          return b.total_final_tax - a.total_final_tax;
        case 'amount_asc':
          return a.total_final_tax - b.total_final_tax;
        default:
          return 0;
      }
    });

    setFilteredCalculations(filtered);
  };

  const handleDownloadPDF = async (calcId) => {
    try {
      const response = await calculationsAPI.downloadPDF(calcId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tarsos_mokestis_${calcId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "PDF atsisiųstas!",
        description: "Patikrinkite savo atsisiuntimus"
      });
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko atsisiųsti PDF",
        variant: "destructive"
      });
    }
  };

  const handleSendEmail = async (calcId) => {
    try {
      const response = await calculationsAPI.generatePDF(calcId);
      
      if (response.data.email_configured) {
        toast({
          title: "El. laiškas išsiųstas!",
          description: "Patikrinkite savo el. paštą"
        });
      } else {
        toast({
          title: "PDF sugeneruotas",
          description: "El. pašto paslauga dar nesukonfigūruota",
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Klaida",
        description: "Nepavyko išsiųsti el. laiško",
        variant: "destructive"
      });
    }
  };

  const getTotalStats = () => {
    if (calculations.length === 0) return { total: 0, average: 0, highest: 0 };
    
    const total = calculations.reduce((sum, calc) => sum + calc.total_final_tax, 0);
    const average = total / calculations.length;
    const highest = Math.max(...calculations.map(calc => calc.total_final_tax));
    
    return { total, average, highest };
  };

  const stats = getTotalStats();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-green-600" />
            <span className="text-xl font-semibold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={logout}>Atsijungti</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <FileText className="w-8 h-8 text-green-600" />
            Skaičiavimų istorija
          </h1>
          <p className="text-gray-600">
            Visi jūsų taršos mokesčio skaičiavimai ir ataskaitos
          </p>
        </div>

        {/* Statistics Cards */}
        {calculations.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Viso skaičiavimų
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{calculations.length}</div>
                <p className="text-xs text-gray-500 mt-1">Per visą laikotarpį</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Vidutinis mokestis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">€{stats.average.toFixed(2)}</div>
                <p className="text-xs text-gray-500 mt-1">Per skaičiavimą</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Didžiausias mokestis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">€{stats.highest.toFixed(2)}</div>
                <p className="text-xs text-gray-500 mt-1">Maksimali suma</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtrai ir paieška
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Ieškoti pagal datą ar sumą..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Naujausi pirma</SelectItem>
                  <SelectItem value="date_asc">Seniausi pirma</SelectItem>
                  <SelectItem value="amount_desc">Didžiausia suma pirma</SelectItem>
                  <SelectItem value="amount_asc">Mažiausia suma pirma</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterByAmount} onValueChange={setFilterByAmount}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Visos sumos</SelectItem>
                  <SelectItem value="low">&lt; €100</SelectItem>
                  <SelectItem value="medium">€100 - €500</SelectItem>
                  <SelectItem value="high">&gt; €500</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Calculations List */}
        <Card>
          <CardHeader>
            <CardTitle>Skaičiavimai ({filteredCalculations.length})</CardTitle>
            <CardDescription>
              Pasirinkite skaičiavimą norėdami peržiūrėti išsamią informaciją
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Kraunama...</p>
              </div>
            ) : filteredCalculations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchQuery || filterByAmount !== 'all' ? 'Rezultatų nerasta' : 'Dar neturite skaičiavimų'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery || filterByAmount !== 'all' 
                    ? 'Pabandykite pakeisti filtrus arba paieškos kriterijus'
                    : 'Pradėkite skaičiuoti taršos mokestį dabar'
                  }
                </p>
                {!searchQuery && filterByAmount === 'all' && (
                  <Link to="/calculator">
                    <Button className="bg-green-600 hover:bg-green-700">
                      Naujas skaičiavimas
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {format(parseISO(calc.created_at), 'yyyy-MM-dd HH:mm', { locale: lt })}
                          </span>
                          {calc.total_discount > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs text-green-700">
                              <TrendingDown className="w-3 h-3" />
                              Sutaupyta €{calc.total_discount.toFixed(2)}
                            </div>
                          )}
                        </div>

                        <div className="grid md:grid-cols-4 gap-4 mb-3">
                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-xs text-gray-600 mb-1">Atliekų srovių</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {calc.waste_results?.length || 0}
                            </p>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-xs text-gray-600 mb-1">Bazinis mokestis</p>
                            <p className="text-lg font-semibold text-gray-900">
                              €{calc.total_base_tax.toFixed(2)}
                            </p>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-xs text-gray-600 mb-1">Nuolaida</p>
                            <p className="text-lg font-semibold text-green-600">
                              -€{calc.total_discount.toFixed(2)}
                            </p>
                          </div>

                          <div className="bg-green-50 p-3 rounded border border-green-200">
                            <p className="text-xs text-green-700 mb-1 font-medium">Galutinis</p>
                            <p className="text-xl font-bold text-green-600">
                              €{calc.total_final_tax.toFixed(2)}
                            </p>
                          </div>
                        </div>

                        {calc.best_organization && (
                          <div className="text-xs text-gray-600 mt-2">
                            <span className="font-medium">Geriausia organizacija:</span>{' '}
                            {/* Would need to fetch org name from potential_savings */}
                            Sutaupymas galimas
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/calculation/${calc.id}`)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Peržiūrėti
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPDF(calc.id)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendEmail(calc.id)}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        El. paštas
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CalculationHistory;
